export function formatCalendarCellDate(dateInput) {
    const d = new Date(dateInput);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function getMonthKeyFromDateValue(dateInput) {
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return `${date.getFullYear()}-${date.getMonth()}`;
}

export function getSafeDisplayDate(value) {
    if (!value) return null;

    if (value instanceof Date) {
        const parsed = new Date(value.getFullYear(), value.getMonth(), value.getDate());
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const stringValue = String(value).trim();
    const parts = stringValue.split("-");

    if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        const parsed = new Date(year, month - 1, day);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(stringValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getLocalDateString(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return "";
    }

    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
}

export function getWeekKeyFromDate(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return null;
    }

    const dayOfWeek = parsed.getDay();
    const diff = parsed.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(parsed);
    monday.setDate(diff);

    return `${monday.getFullYear()}-${monday.getMonth()}-${monday.getDate()}`;
}

export function getMondayForDate(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return null;
    }

    const dayOfWeek = parsed.getDay();
    const diff = parsed.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(parsed);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);

    return monday;
}

export function getMonthGridStartDate(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    return getMondayForDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
}

export function getMonthGridEndDate(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    const monthGridStart = getMonthGridStartDate(monthDate);
    if (!monthGridStart) {
        return null;
    }

    const gridEnd = new Date(monthGridStart);
    gridEnd.setDate(monthGridStart.getDate() + 41);
    gridEnd.setHours(0, 0, 0, 0);

    return gridEnd;
}

export function getFirstMondayWithinMonth(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const firstMonday = getMondayForDate(firstDayOfMonth);

    if (!firstMonday) {
        return null;
    }

    if (firstMonday.getMonth() !== monthDate.getMonth()) {
        firstMonday.setDate(firstMonday.getDate() + 7);
    }

    firstMonday.setHours(0, 0, 0, 0);
    return firstMonday;
}

export function buildMonthWeekStartDates(monthDateValue, options = {}) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return [];
    }

    const today = getSafeDisplayDate(options.today ?? new Date());
    const maxWeeks = Number(options.maxWeeks) > 0 ? Number(options.maxWeeks) : 6;
    const monthGridStart = getMonthGridStartDate(monthDate);
    const monthGridEnd = getMonthGridEndDate(monthDate);

    if (!monthGridStart || !monthGridEnd) {
        return [];
    }

    const isCurrentMonth = !!today &&
        today.getFullYear() === monthDate.getFullYear() &&
        today.getMonth() === monthDate.getMonth();

    const currentWeekMonday = today ? getMondayForDate(today) : null;
    let startWeek = monthGridStart;
    let endWeek = getMondayForDate(monthGridEnd) || monthGridEnd;

    if (isCurrentMonth && currentWeekMonday && currentWeekMonday > monthGridStart) {
        // Start at the current week rather than the grid's first row: nobody needs the
        // weeks they have already lived through.
        //
        // The END is deliberately NOT clamped to the month. It used to be
        // `getMondayForDate(endOfMonth)`, which for September 2026 is 28 Sep - but
        // FullCalendar renders a fixed six rows (`fixedWeekCount` defaults to true), so
        // the grid's last row starts 5 Oct. That row was rendered and clickable while
        // never appearing in this list, so it was never fetched: bare cells, and a click
        // that only reached the bare-cell handler instead of opening a popup.
        startWeek = currentWeekMonday;
    }

    const weekStarts = [];
    const cursor = new Date(startWeek);

    for (let index = 0; index < maxWeeks && cursor <= endWeek; index += 1) {
        weekStarts.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 7);
    }

    return weekStarts;
}

export function buildMonthWeekData(monthDateValue, scheduleItems = []) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return {};
    }

    const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const monthWeekData = {};

    const cursor = new Date(startOfMonth);
    while (cursor <= endOfMonth) {
        const weekKey = getWeekKeyFromDate(cursor);
        if (weekKey && !monthWeekData[weekKey]) {
            monthWeekData[weekKey] = [];
        }
        cursor.setDate(cursor.getDate() + 1);
    }

    (Array.isArray(scheduleItems) ? scheduleItems : []).forEach((scheduleItem) => {
        const weekKey = getWeekKeyFromDate(scheduleItem?.BookingDate);
        if (!weekKey) {
            return;
        }

        if (!monthWeekData[weekKey]) {
            monthWeekData[weekKey] = [];
        }

        monthWeekData[weekKey].push(scheduleItem);
    });

    return monthWeekData;
}

export function formatDateString(dateString) {
    if (!dateString) {
        return null;
    }

    const date = getSafeDisplayDate(dateString);

    if (!date || Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}
/**
 * Height of the chrome pinned to the top of the month scroll container: the sticky
 * toolbar plus the sticky `Mon…Sun` header row. A week row sitting under either of
 * them is covered, not visible.
 *
 * Computed from each element's resting offset (its CSS `top`) plus its own height,
 * NOT from its current `getBoundingClientRect().top`. Those differ: an element that
 * has not been scrolled far enough to stick yet still reports its flow position, so a
 * rect-based inset changes as the user scrolls and the spacing it drives visibly
 * shifts. `top + height` is where the element comes to rest once stuck, which is the
 * same number at any scroll position.
 *
 * Read from the elements rather than from `--calendar-toolbar-height` /
 * `--calendar-toolbar-top`, because a second copy of those numbers here would drift
 * silently the moment anyone tuned the CSS.
 *
 * @param {HTMLElement} rootElement calendar root (contains .fc-header-toolbar)
 * @returns {number} pixels of cover at the top of the scroll port
 */
function getStickyTopInset(rootElement) {
    if (!rootElement || typeof window.getComputedStyle !== "function") {
        return 0;
    }

    const stickyParts = [
        rootElement.querySelector(".fc-header-toolbar"),
        // The sticky rule lands on the cell, not the row - FullCalendar's own
        // `.fc-scrollgrid-section-sticky > *`. Measuring the `tr` would report
        // `position: static` and be skipped.
        rootElement.querySelector("tr.fc-scrollgrid-section-header > th"),
    ];

    return stickyParts.reduce((inset, element) => {
        if (!element) {
            return inset;
        }

        const style = window.getComputedStyle(element);

        if (style.position !== "sticky") {
            return inset;
        }

        const restingTop = parseFloat(style.top);

        if (Number.isNaN(restingTop)) {
            return inset;
        }

        return Math.max(inset, restingTop + element.getBoundingClientRect().height);
    }, 0);
}

/**
 * Monday of every week row the user can see any part of — anything overlapping the
 * area between the pinned chrome and the bottom of the scroll container.
 *
 * Partly visible rows count. The manual refresh is driven by "these are the weeks on
 * my screen, bring them up to date", and a row showing three of its seven days is
 * still a row being looked at. Only a row completely hidden behind the sticky toolbar
 * and weekday header, or completely below the fold, is excluded.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {HTMLElement|Window} scrollContainer the month scroll container
 * @returns {Date[]} week-start Dates, top row first; empty when nothing qualifies
 */
export function getVisibleWeekStarts(rootElement, scrollContainer) {
    if (!rootElement) {
        return [];
    }

    const isWindowScroll = !scrollContainer || scrollContainer === window;
    const viewportRect = isWindowScroll
        ? { top: 0, bottom: window.innerHeight }
        : scrollContainer.getBoundingClientRect();

    const topBound = viewportRect.top + getStickyTopInset(rootElement);
    const bottomBound = viewportRect.bottom;

    const rows = getRenderedWeekRows(rootElement);

    return rows
        .filter((row) => {
            const rect = row.getBoundingClientRect();
            // Any overlap with the usable area counts. A zero-height row (mid-render)
            // would otherwise pass trivially, so it is excluded.
            return rect.height > 0 && rect.bottom > topBound && rect.top < bottomBound;
        })
        .map((row) => row.querySelector(".fc-daygrid-day[data-date]"))
        .map((cell) => getMondayForDate(cell?.getAttribute("data-date")))
        .filter(Boolean);
}
// Class marking a week row as the start of a scroll page. Kept here next to the code
// that applies it; main-calendar.css hangs `scroll-snap-align: start` off it.
export const WEEK_SNAP_POINT_CLASS = "is-week-snap-point";

// Class marking a week row as trimmed off the top of the current month, applied by
// applyPastWeekRowVisibility. main-calendar.css hangs `display: none` off it.
export const WEEK_HIDDEN_CLASS = "is-week-hidden";

/**
 * The week rows FullCalendar drew that are still on show.
 *
 * Trimmed rows are `display: none`, so anything that measures the grid - how tall a
 * row is, how many fit a screenful, which weeks the user is looking at - has to work
 * from this list or it measures a zero-height row and draws the wrong conclusion. The
 * class is checked rather than the height, so the answer does not depend on whether
 * the stylesheet has painted yet.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @returns {HTMLElement[]} showing week rows, top row first
 */
function getRenderedWeekRows(rootElement) {
    if (!rootElement) {
        return [];
    }

    return Array.from(rootElement.querySelectorAll(".fc-daygrid-body tbody tr"))
        .filter((row) => !row.classList.contains(WEEK_HIDDEN_CLASS));
}

// How much of a week row has to be on screen before it names the month on the toolbar.
// Half: the title then changes once, halfway through the row, rather than flickering at
// the boundary or lagging a whole row behind.
export const MONTH_AT_TOP_MIN_VISIBLE_RATIO = 0.5;

/**
 * The Monday of the week a rendered row covers, read from its first dated cell.
 *
 * @param {HTMLElement} row a `tr` from the day grid
 * @returns {Date|null} that week's Monday
 */
function getRowWeekStart(row) {
    const cell = row?.querySelector(".fc-daygrid-day[data-date]");

    return getMondayForDate(cell?.getAttribute("data-date"));
}

/**
 * Trim the weeks that are wholly behind the user off the top of the current month.
 *
 * FullCalendar's month grid always runs from the Monday on or before the 1st, so a
 * month whose 1st falls late in a week opens on rows nobody can act on: September
 * 2026 starts on a Tuesday, so its grid leads with 31 Aug-6 Sep and 7-13 Sep, both
 * entirely gone by the 17th.
 *
 * Those rows were never populated either - buildMonthWeekStartDates already starts the
 * current month's fetch at the current week - so they rendered as bare, clickable
 * cells that no request would ever fill. Hiding them makes the grid agree with the
 * data behind it.
 *
 * Whole rows only, and the current week is always kept: on the 17th the user still
 * sees 14-16 greyed out beside it, because that is the week they are in.
 *
 * ONLY the current month is trimmed. A past month is fetched in full, so hiding its
 * rows would blank a grid that has real data behind it, and a future month has no past
 * weeks to hide.
 *
 * Run this BEFORE applyWeekSnapPoints - snap points are placed over the rows that
 * survive.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {Date|string} monthDateValue any date in the rendered month
 * @param {Date|string} [todayValue] today; injectable so tests need not travel in time
 * @returns {number} how many rows were hidden
 */
export function applyPastWeekRowVisibility(rootElement, monthDateValue, todayValue) {
    if (!rootElement) {
        return 0;
    }

    const rows = Array.from(rootElement.querySelectorAll(".fc-daygrid-body tbody tr"));

    if (!rows.length) {
        return 0;
    }

    const monthDate = getSafeDisplayDate(monthDateValue);
    const today = getSafeDisplayDate(todayValue ?? new Date());
    const currentWeekMonday = today ? getMondayForDate(today) : null;
    const isCurrentMonth = !!monthDate && !!today &&
        monthDate.getFullYear() === today.getFullYear() &&
        monthDate.getMonth() === today.getMonth();

    // Clearing rather than returning early: FullCalendar reuses the same `tr` elements
    // as the user pages off the current month, and a stale class would leave October
    // opening on a gap where September's trimmed rows used to be.
    if (!isCurrentMonth || !currentWeekMonday) {
        rows.forEach((row) => row.classList.remove(WEEK_HIDDEN_CLASS));
        return 0;
    }

    let hiddenCount = 0;

    rows.forEach((row) => {
        const cell = row.querySelector(".fc-daygrid-day[data-date]");
        const rowWeekStart = getMondayForDate(cell?.getAttribute("data-date"));
        // Comparing Mondays is what keeps this to whole rows: a row starting before the
        // current week's Monday also ends before it, since every row is exactly 7 days.
        const isFullyPast = !!rowWeekStart && rowWeekStart.getTime() < currentWeekMonday.getTime();

        row.classList.toggle(WEEK_HIDDEN_CLASS, isFullyPast);

        if (isFullyPast) {
            hiddenCount += 1;
        }
    });

    return hiddenCount;
}

/**
 * Hide every week row after the last one the range covers, and report how many.
 *
 * The grid is asked for a whole number of weeks, so in principle it never draws past its
 * own range. In practice rows turn up beyond it, and being out of range they render as
 * empty cells that read as blank weeks under the last real one.
 *
 * **Position, not date.** The first attempt hid rows whose Monday was at or after the end,
 * read from `.fc-daygrid-day[data-date]` - and missed entirely, because those trailing
 * cells carry no `data-date` to read. A row with nothing to identify it is exactly the
 * kind this has to remove, so the rule is "everything after the last row that IS in range",
 * which catches dated and dateless rows alike.
 *
 * `rangeEndValue` is EXCLUSIVE, matching getContinuousRangeEndDate: a row starting on it is
 * already outside.
 *
 * Hides nothing when no row is in range. That means the grid is mid-render or the end is
 * wrong, and blanking every row would be far worse than leaving the extra ones showing.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {Date|string} rangeEndValue exclusive end of the range
 * @returns {number} how many rows were hidden
 */
export function applyOutOfRangeWeekRowVisibility(rootElement, rangeEndValue) {
    if (!rootElement) {
        return 0;
    }

    const rows = Array.from(rootElement.querySelectorAll(".fc-daygrid-body tbody tr"));

    if (!rows.length) {
        return 0;
    }

    const rangeEnd = getSafeDisplayDate(rangeEndValue);

    // No usable end: clear rather than guess, so a transient null cannot leave rows hidden.
    if (!rangeEnd) {
        rows.forEach((row) => row.classList.remove(WEEK_HIDDEN_CLASS));
        return 0;
    }

    let lastInRangeIndex = -1;

    rows.forEach((row, index) => {
        const weekStart = getRowWeekStart(row);

        if (weekStart && weekStart.getTime() < rangeEnd.getTime()) {
            lastInRangeIndex = index;
        }
    });

    if (lastInRangeIndex === -1) {
        rows.forEach((row) => row.classList.remove(WEEK_HIDDEN_CLASS));
        return 0;
    }

    let hiddenCount = 0;

    rows.forEach((row, index) => {
        const isAfterRange = index > lastInRangeIndex;

        row.classList.toggle(WEEK_HIDDEN_CLASS, isAfterRange);

        if (isAfterRange) {
            hiddenCount += 1;
        }
    });

    return hiddenCount;
}

/**
 * Tag every Nth week row as a scroll snap point, so one scroll gesture advances a
 * whole screenful of weeks instead of landing part-way through a row.
 *
 * N is measured, not assumed: it is how many whole rows fit below the pinned chrome,
 * which is roughly 3 on a laptop and 6 on a tall monitor - the container height is
 * uncapped (`calc(100vh - 124px)`), so this genuinely varies by screen. Recompute
 * on resize and whenever the month re-renders.
 *
 * The browser does all the actual scrolling. Nothing here intercepts wheel or touch
 * events - that would fight both native momentum and the overscroll month gesture,
 * which needs those same events.
 *
 * Does NOT set `scroll-padding-top`. It used to, at the measured inset (109px on the
 * current design: a 74px toolbar plus the ~35px weekday row pinned below it). That
 * padding shifts every snap position 109px earlier in scroll terms - a row comes to
 * rest 109px below the scrollport top instead of at it - which put a systematic 109px
 * upward bias on wherever a gesture landed. Dragging the scrollbar down and releasing
 * mid-page visibly pulled the grid back up, so it was removed.
 *
 * The inset is still measured, because the usable height it leaves is what decides how
 * many rows fit a page.
 *
 * TRADE-OFF, still live: `scroll-padding` also applies to `scrollIntoView`, which
 * navigateToMonthDate uses with `block: "start"` to align the carried week after a
 * month change. Without the padding that row aligns to the container's top edge, which
 * is underneath the pinned toolbar and weekday header. Compensate there if it shows.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {HTMLElement|Window} scrollContainer the month scroll container
 * @returns {number} rows per page, or 0 when the grid is not measurable yet
 */
export function applyWeekSnapPoints(rootElement, scrollContainer) {
    if (!rootElement) {
        return 0;
    }

    const isWindowScroll = !scrollContainer || scrollContainer === window;
    const viewportRect = isWindowScroll
        ? { top: 0, bottom: window.innerHeight }
        : scrollContainer.getBoundingClientRect();

    const allRows = Array.from(rootElement.querySelectorAll(".fc-daygrid-body tbody tr"));
    const rows = getRenderedWeekRows(rootElement);

    if (!rows.length) {
        return 0;
    }

    // The first SHOWING row, not `allRows[0]`. On the current month `allRows[0]` can be
    // a trimmed row, which measures 0 and would trip the guard below - snap points
    // would then be skipped entirely and paged scrolling would silently stop working.
    const rowHeight = rows[0].getBoundingClientRect().height;
    const stickyInset = getStickyTopInset(rootElement);
    const usableHeight = (viewportRect.bottom - viewportRect.top) - stickyInset;

    // Mid-render the grid can report zero heights; tagging rows from that would put
    // snap points in the wrong places until something forced a recalculation.
    if (rowHeight <= 0 || usableHeight <= 0) {
        return 0;
    }

    const rowsPerPage = Math.max(1, Math.floor(usableHeight / rowHeight));

    // Paged over showing rows only: a trimmed row occupies no space, so counting it
    // towards a page would leave the first page short by however many were trimmed.
    let visibleIndex = 0;

    allRows.forEach((row) => {
        if (row.classList.contains(WEEK_HIDDEN_CLASS)) {
            row.classList.remove(WEEK_SNAP_POINT_CLASS);
            return;
        }

        row.classList.toggle(WEEK_SNAP_POINT_CLASS, visibleIndex % rowsPerPage === 0);
        visibleIndex += 1;
    });

    return rowsPerPage;
}
/**
 * How many whole weeks a continuous range spans, from the Monday of `startValue` up to
 * `endValue`.
 *
 * Feeds the continuous view's `duration: { weeks: N }`. Weeks, not months, on purpose:
 * a weeks-duration dayGrid view aligns its range to `firstDay`, so the grid begins on
 * the current week's Monday. A months duration would align to the 1st and bring back
 * the dead leading rows that applyPastWeekRowVisibility exists to hide.
 *
 * Ceiled, so a range ending mid-week still renders that week. `maxWeeks` is a safety
 * valve, not a product rule: it stops a bad ZoneCurrentDate turning into a grid with
 * thousands of rows.
 *
 * @param {Date|string} startValue any date in the first week
 * @param {Date|string} endValue exclusive end of the range
 * @param {{maxWeeks?: number}} [options]
 * @returns {number} week count, or 0 when the range is empty or unusable
 */
export function getContinuousWeekCount(startValue, endValue, options = {}) {
    const startMonday = getMondayForDate(startValue);
    const endDate = getSafeDisplayDate(endValue);

    if (!startMonday || !endDate) {
        return 0;
    }

    const maxWeeks = Number(options.maxWeeks) > 0 ? Number(options.maxWeeks) : 62;
    const spanMs = endDate.getTime() - startMonday.getTime();

    if (spanMs <= 0) {
        return 0;
    }

    // Ceil rather than round: a DST shift makes a year an hour short of a whole number
    // of weeks, and rounding down there would drop the final week.
    const weeks = Math.ceil(spanMs / (7 * 24 * 60 * 60 * 1000));

    return Math.min(maxWeeks, Math.max(1, weeks));
}

/**
 * The Monday of the week row that OWNS the top of the scrollport.
 *
 * Deliberately not `getVisibleWeekStarts(...)[0]`. That one counts a row as visible on any
 * overlap at all, which is right for deciding what to fetch - a row showing three of its
 * seven days is still being looked at - and wrong for naming the month on the toolbar.
 *
 * Two ways it went wrong:
 *
 *  - Scrolling down, the departing row stays "visible" until it is completely behind the
 *    pinned chrome, so the title kept naming the old month for a whole row of scrolling.
 *  - Jumping to a month, the row above the target ends exactly on the top bound. Sticky
 *    insets and scroll offsets are fractional, so a sub-pixel undershoot left a sliver of
 *    it showing and the title named ITS month instead. Picking June 2027 landed on May:
 *    1 June is a Tuesday, so its row starts 31 May, and the row above that is 24-30 May.
 *
 * So a row has to be at least half on screen to name the month. That also removes any
 * flicker at the boundary: the title changes once, halfway through the row.
 *
 * Falls back to the first overlapping row, for the short final row at the bottom of the
 * range where nothing reaches half.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {HTMLElement|Window} scrollContainer the month scroll container
 * @param {{minVisibleRatio?: number}} [options]
 * @returns {Date|null} that row's Monday
 */
export function getWeekStartAtScrollTop(rootElement, scrollContainer, options = {}) {
    if (!rootElement) {
        return null;
    }

    const rows = getRenderedWeekRows(rootElement);

    if (!rows.length) {
        return null;
    }

    const isWindowScroll = !scrollContainer || scrollContainer === window;
    const viewportRect = isWindowScroll
        ? { top: 0, bottom: window.innerHeight }
        : scrollContainer.getBoundingClientRect();

    const topBound = viewportRect.top + getStickyTopInset(rootElement);
    const bottomBound = viewportRect.bottom;
    const minVisibleRatio = Number.isFinite(options.minVisibleRatio)
        ? options.minVisibleRatio
        : MONTH_AT_TOP_MIN_VISIBLE_RATIO;

    let firstOverlappingRow = null;

    for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const rect = row.getBoundingClientRect();

        if (rect.height <= 0 || rect.bottom <= topBound || rect.top >= bottomBound) {
            continue;
        }

        if (!firstOverlappingRow) {
            firstOverlappingRow = row;
        }

        const visibleHeight = Math.min(rect.bottom, bottomBound) - Math.max(rect.top, topBound);

        if (visibleHeight >= rect.height * minVisibleRatio) {
            return getRowWeekStart(row);
        }
    }

    return getRowWeekStart(firstOverlappingRow);
}

/**
 * Exclusive end of the continuous range: `days` days from today, extended to the END OF
 * THAT MONTH, never past the navigable range.
 *
 * The month rounding is what stops the grid stopping mid-month. A plain 365-day window
 * ends on whatever date it lands on - 19 Sept 2027 for a mid-September start - so the
 * final month showed only its first nineteen days and then stopped. Rounding to the end
 * of the month the window lands in means the last month a reader can scroll to is always
 * a whole month.
 *
 * Measured from the window's LAST DAY (`today + days - 1`), not from its exclusive end.
 * Using the exclusive end would roll a window finishing exactly on the 1st into the whole
 * of the following month - an extra month nobody asked for.
 *
 * The week rounding is separate and lives in getContinuousWeekCount, which ceils to whole
 * weeks; together they give a complete final month AND a complete final week.
 *
 * This deliberately gives back some of what was taken off when this stopped using
 * getCalendarNavigationEndDate (up to ~29 days, 53 weeks becoming 55 for a mid-month
 * start). It is still tighter than that helper, which rounds to `month + 13, day 1` and
 * so runs a further month out when today is early in a month.
 *
 * Still clamped to the navigable end, so the grid can never render a week outside it. In
 * practice the clamp does not bind - thirteen months is further out than a year rounded
 * up a month - but the guarantee belongs in one place rather than at the call site.
 *
 * Calendar arithmetic (`setDate`), not milliseconds, so a DST change inside the year does
 * not shift the boundary by an hour.
 *
 * @param {Date|string} todayValue today, the anchor the range is measured from
 * @param {Date|string} navigationEndDate exclusive end of the navigable range, or null
 * @param {{days?: number}} [options] window length, default 365
 * @returns {Date|null} exclusive end date
 */
export function getContinuousRangeEndDate(todayValue, navigationEndDate, options = {}) {
    const today = getSafeDisplayDate(todayValue);

    if (!today) {
        return null;
    }

    const days = Number(options.days) > 0 ? Number(options.days) : 365;

    // The window's last INCLUSIVE day. `days - 1` matters: a 365-day window starting on
    // the 1st ends on the last day of the twelfth month, and measuring from the exclusive
    // end would roll that into the whole of the month after.
    const windowLastDay = new Date(today);
    windowLastDay.setDate(today.getDate() + days - 1);

    // First day of the month AFTER the one the window ends in - i.e. that month, complete.
    const monthEnd = new Date(windowLastDay.getFullYear(), windowLastDay.getMonth() + 1, 1);
    monthEnd.setHours(0, 0, 0, 0);

    const navigationEnd = getSafeDisplayDate(navigationEndDate);

    return (navigationEnd && navigationEnd.getTime() < monthEnd.getTime())
        ? navigationEnd
        : monthEnd;
}

/**
 * Every week start in a continuous range, ordered, beginning at the Monday of
 * `startValue`.
 *
 * The lazy queue works by walking an ordered list of week starts and fetching the first
 * one that has no data yet. In month mode that list came from buildMonthWeekStartDates
 * and was at most six long; a continuous grid needs the same list over its whole range,
 * or the queue simply has nothing to say about a week the user has scrolled to and
 * stops fetching.
 *
 * @param {Date|string} startValue any date in the first week
 * @param {number} weekCount how many weeks the range spans
 * @returns {Date[]} week-start Dates, earliest first
 */
export function buildContinuousWeekStartDates(startValue, weekCount) {
    const startMonday = getMondayForDate(startValue);
    const count = Number(weekCount);

    if (!startMonday || !Number.isFinite(count) || count <= 0) {
        return [];
    }

    const weekStarts = [];
    const cursor = new Date(startMonday);

    for (let index = 0; index < count; index += 1) {
        weekStarts.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 7);
    }

    return weekStarts;
}

/**
 * The month a week belongs to, as a Date on the 1st.
 *
 * A week row straddling a month boundary belongs to whichever month holds **four or
 * more** of its days, which is always the month of its Thursday - the same rule ISO
 * week numbering uses.
 *
 * Going by the first day instead would lag by a week: 28 Sep-4 Oct starts in September
 * but has four of its seven days in October, so the toolbar would still read September
 * while the row on screen is mostly October.
 *
 * @param {Date|string} weekStartValue any date in the week
 * @returns {Date|null} the 1st of the owning month
 */
export function getWeekOwningMonth(weekStartValue) {
    const weekStart = getMondayForDate(weekStartValue);

    if (!weekStart) {
        return null;
    }

    const thursday = new Date(weekStart);
    thursday.setDate(weekStart.getDate() + 3);

    return new Date(thursday.getFullYear(), thursday.getMonth(), 1);
}

/**
 * Scroll `rowElement` to the top of the scrollport, clear of the pinned chrome.
 *
 * Replaces `scrollIntoView({ block: "start" })` for this job. That aligns to the
 * container's top edge, which sits underneath the sticky toolbar and weekday header, so
 * the row it lands on is covered. `scroll-padding-top` used to fix that globally, but it
 * also biased every scroll-snap landing upward by the same 109px and was removed; doing
 * the arithmetic here keeps the correction where it is wanted and nowhere else.
 *
 * Works in deltas from `getBoundingClientRect`, so it needs no assumptions about the
 * offsetParent chain between the row and the scroll container.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {HTMLElement|Window} scrollContainer the month scroll container
 * @param {HTMLElement} rowElement the week row to bring to the top
 * @param {ScrollBehavior} [behavior] "auto" (default) or "smooth"
 * @returns {boolean} whether a scroll was issued
 */
export function scrollWeekRowToTop(rootElement, scrollContainer, rowElement, behavior = "auto") {
    if (!rootElement || !rowElement || typeof rowElement.getBoundingClientRect !== "function") {
        return false;
    }

    const stickyInset = getStickyTopInset(rootElement);
    const rowTop = rowElement.getBoundingClientRect().top;
    const isWindowScroll = !scrollContainer || scrollContainer === window;

    if (isWindowScroll) {
        if (typeof window.scrollBy !== "function") {
            return false;
        }

        window.scrollBy({ top: rowTop - stickyInset, behavior });
        return true;
    }

    if (typeof scrollContainer.scrollTo !== "function") {
        return false;
    }

    const containerTop = scrollContainer.getBoundingClientRect().top;
    const delta = rowTop - containerTop - stickyInset;

    scrollContainer.scrollTo({ top: (scrollContainer.scrollTop || 0) + delta, behavior });
    return true;
}

/**
 * The first week start in `weekStartDates` that OWNS `monthDateValue`.
 *
 * "Owns" by the same four-or-more-days rule the toolbar title uses - see
 * getWeekOwningMonth - which is what makes jumping to a month land on a grid the title
 * agrees with.
 *
 * The obvious alternative, the row containing the 1st, is wrong whenever the 1st falls
 * late in a week: 1 Aug 2027 is a SUNDAY, so its row is 26 Jul-1 Aug and six of those
 * seven days are July. Jumping there left the title reading July after picking August,
 * and it wedged the next arrow - from July it targeted August, landed on a row still
 * owned by July, and pressing it again did nothing.
 *
 * Every month has at least four Thursdays, so a month fully inside the range always has
 * an owning week. Returns null when the month lies outside it.
 *
 * @param {Date[]} weekStartDates ordered week starts, as built for the continuous range
 * @param {Date|string} monthDateValue any date in the wanted month
 * @returns {Date|null} that week's Monday
 */
export function findWeekStartOwningMonth(weekStartDates, monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!Array.isArray(weekStartDates) || !monthDate) {
        return null;
    }

    const targetMonthTime = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).getTime();

    return weekStartDates.find((weekStart) => {
        const owningMonth = getWeekOwningMonth(weekStart);
        return owningMonth && owningMonth.getTime() === targetMonthTime;
    }) || null;
}

/**
 * The week row element whose Monday matches `weekStartValue`, or null.
 *
 * Used to keep the reader's place across a month change: the week they were looking at
 * is aligned to the top of the new grid rather than jumping to whichever week happens
 * to come first.
 *
 * Returns null when that week is not in the rendered grid, which is a normal outcome,
 * not an error. Month grids overlap at the boundary most of the time - September 2026
 * ends with the row beginning 28 Sep and October's grid starts on that same row - but
 * not when a month ends exactly on a Sunday (January and February 2027), where the next
 * grid starts on a fresh week. Callers fall back to the top of the grid.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {Date|string} weekStartValue any date in the wanted week
 * @returns {HTMLElement|null} the `tr` for that week
 */
export function findWeekRowElement(rootElement, weekStartValue) {
    const weekStart = getMondayForDate(weekStartValue);

    if (!rootElement || !weekStart) {
        return null;
    }

    // Trimmed rows are excluded on purpose: scrolling to one would align the grid to a
    // row of zero height. Callers already treat null as "start at the top instead".
    const rows = getRenderedWeekRows(rootElement);

    return rows.find((row) => {
        const cell = row.querySelector(".fc-daygrid-day[data-date]");
        const rowWeekStart = getMondayForDate(cell?.getAttribute("data-date"));
        return rowWeekStart && rowWeekStart.getTime() === weekStart.getTime();
    }) || null;
}
